import { useState, useRef, useEffect } from 'react';
import { useListWorkspacesQuery, useSwitchWorkspaceMutation } from '@api/workspaceApi';
import toast from 'react-hot-toast';
import { ChevronDown, Check, Plus, Building2 } from 'lucide-react';

const WorkspaceSwitcher = ({ currentWorkspaceId, onCreateNew }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { data } = useListWorkspacesQuery();
  const [switchWorkspace] = useSwitchWorkspaceMutation();

  const workspaces = data?.data || [];
  const current = workspaces.find((w) => w.id === currentWorkspaceId) || workspaces[0];

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSwitch = async (ws) => {
    if (ws.id === currentWorkspaceId) { setOpen(false); return; }
    try {
      await switchWorkspace(ws.id).unwrap();
      localStorage.setItem('activeWorkspaceId', ws.id);
      toast.success(`Switched to ${ws.name}`);
      setOpen(false);
      window.location.reload(); // Reload to refresh all workspace-scoped data
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Failed to switch workspace');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-100 transition-colors max-w-[220px]">
        <div className="w-7 h-7 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          {current?.logo ? (
            <img src={current.logo} alt="" className="w-7 h-7 rounded-md object-cover" />
          ) : (
            <Building2 size={14} />
          )}
        </div>
        <span className="text-sm font-semibold text-surface-800 truncate">{current?.name || 'Workspace'}</span>
        <ChevronDown size={14} className={`text-surface-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-xl shadow-modal border border-surface-200 py-2 z-50 animate-fade-in">
          <p className="px-3 py-1.5 text-xs font-medium text-surface-500 uppercase tracking-wider">Workspaces</p>
          {workspaces.map((ws) => (
            <button key={ws.id} onClick={() => handleSwitch(ws)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-50 transition-colors text-left">
              <div className="w-7 h-7 rounded-md bg-brand-gradient text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">
                {ws.logo ? <img src={ws.logo} alt="" className="w-7 h-7 rounded-md object-cover" /> : ws.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-surface-800 truncate">{ws.name}</p>
                <p className="text-xs text-surface-500">{ws.memberCount} member{ws.memberCount !== 1 ? 's' : ''} · {ws.plan}</p>
              </div>
              {ws.id === currentWorkspaceId && <Check size={14} className="text-primary-600 flex-shrink-0" />}
            </button>
          ))}
          <hr className="my-2 border-surface-100" />
          <button onClick={() => { setOpen(false); onCreateNew?.(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-50 transition-colors text-left">
            <div className="w-7 h-7 rounded-md border-2 border-dashed border-surface-300 flex items-center justify-center">
              <Plus size={12} className="text-surface-500" />
            </div>
            <span className="text-sm font-medium text-surface-600">Create workspace</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;
